import React, { useEffect, useState } from 'react';
import { useDebounce } from '@/hooks';
import ArrowIcon from '@/public/icons/arrow-left.svg';
import styles from './ScrollToTopButton.module.scss';

interface ScrollToTopButtonProps<T extends HTMLElement> {
	scrollableContainer: React.RefObject<T> | 'window';
	itemRef: React.RefObject<T>;
	itemQuantity?: number;
	className?: string;
	customScrollWindowPositon?: number;
}

export const ScrollToTopButton = <T extends HTMLElement>({
	scrollableContainer,
	itemRef,
	itemQuantity = 4,
	className,
	customScrollWindowPositon,
}: ScrollToTopButtonProps<T>) => {
	const scrollToTop = () => {
		if (scrollableContainer === 'window') {
			window.scrollTo({
				top: customScrollWindowPositon ?? 0,
				behavior: 'smooth',
			});
		} else {
			scrollableContainer.current?.scrollTo({ top: 0, behavior: 'smooth' });
		}
	};

	const [buttonVisible, setButtonVisible] = useState(false);

	const debouncedScrollHandler = useDebounce(() => {
		const firstItem = itemRef.current;
		if (!firstItem) return;

		const rowHeight = firstItem.getBoundingClientRect().height;
		const threshold = rowHeight * itemQuantity;

		let currentScroll: number;

		if (scrollableContainer === 'window') {
			currentScroll = window.scrollY;
		} else {
			currentScroll = scrollableContainer.current?.scrollTop ?? 0;
		}

		setButtonVisible(currentScroll >= threshold);
	}, 100);

	useEffect(() => {
		const target =
			scrollableContainer === 'window' ? window : scrollableContainer.current;

		if (!target) return;

		target.addEventListener('scroll', debouncedScrollHandler);

		return () => {
			target.removeEventListener('scroll', debouncedScrollHandler);
		};
	}, [scrollableContainer, debouncedScrollHandler]);

	if (!buttonVisible) return <></>;

	return (
		<button
			type='button'
			className={`${styles['scroll-btn']} ${className}`}
			onClick={scrollToTop}
		>
			<ArrowIcon className={styles['scroll-btn__icon']} />
		</button>
	);
};
